import { useEffect, useState } from 'react';
import type { AdditionalServiceType } from 'graphql-ts-types';
import { noop } from 'lodash-es';

import { resetCouponInStorage } from 'common/components/Coupon/storage';
import { CartMessageCode } from 'common/enums/Cart';
import { useAddToCartSubmit } from 'common/hooks/cart';
import { getMessageFromError } from 'common/utils/error-handlers';
import type { AddToCartProps } from '../types';

interface UseSubmitHandlerOptions extends Pick<
  AddToCartProps,
  | 'sku'
  | 'name'
  | 'variantName'
  | 'materialType'
  | 'delivery'
  | 'prices'
  | 'assembly'
  | 'energyClass'
  | 'images'
  | 'tracking'
  | 'cartGrandTotal'
  | 'alreadyInCart'
  | 'onAddToCart'
  | 'bestOffer'
  | 'onItemNotSellable'
  | 'accessorySliderProps'
  | 'disableDrawerOpening'
> {
  selectedServices: AdditionalServiceType[];
  quantity: string;
  couponCode: string;
}

export const useSubmitHandler = ({
  quantity,
  assembly,
  tracking,
  alreadyInCart,
  bestOffer,
  onItemNotSellable,
  onAddToCart = noop,
  ...restParams
}: UseSubmitHandlerOptions) => {
  const {
    sku,
    cartGrandTotal,
    accessorySliderProps,
    disableDrawerOpening,
    selectedServices,
    couponCode,
    ...article
  } = restParams;
  const [addedToCart, setAddedToCart] = useState(alreadyInCart);
  const [addToCartErrorMessage, setAddToCartErrorMessage] = useState(null);
  useEffect(() => {
    if (addToCartErrorMessage === CartMessageCode.ItemNotSellable) {
      onItemNotSellable();
    }
  }, [addToCartErrorMessage]);
  const { isLoading, handler } = useAddToCartSubmit({
    article,
    sku,
    quantity: +quantity,
    tracking: { ...tracking, shopId: bestOffer?.shop?.id },
    cartGrandTotal,
    accessorySliderProps,
    disableDrawerOpening,
    assembly,
    selectedServices,
    couponCode,
    onSuccess: cart => {
      setAddToCartErrorMessage(null);
      setAddedToCart(true);
      onAddToCart(cart);
      resetCouponInStorage(sku);
    },
    onError: (error: unknown) => {
      const message = getMessageFromError(error);
      if (message) {
        setAddToCartErrorMessage(message);
      }
    },
  });

  return {
    isLoading,
    addedToCart,
    addToCartErrorMessage,
    handleSubmit: handler,
  };
};
